import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { useQari } from "@/context/QariContext";
import { useLang } from "@/context/LangContext";
import { buildChapters } from "@/lib/audiobook";
import { useManasikContent } from "@/hooks/useManasikContent";
import { AUDIOBOOK_UI } from "@/data/audiobookI18n";
import { chaptersSig, countSegments, downloadPack, deletePack, getPack, packState, offlineSegmentUrl, isNetworkError } from "@/lib/audiobookOffline";
import { getLearn, putLearn } from "@/lib/learnSync";
import { toast } from "sonner";
import { logWarn } from "@/lib/log";

const POS_KEY = "audiobook_pos";
const RATE_KEY = "audiobook_rate";
export const RATES = [0.8, 1, 1.25];
const BASE = API.replace(/\/api$/, "");
const Ctx = createContext(null);

const readPos = () => {
  try {
    const p = JSON.parse(localStorage.getItem(POS_KEY));
    return p && Number.isInteger(p.c) ? { c: p.c, s: Number.isInteger(p.s) ? p.s : 0, t: p.t || 0 } : null;
  } catch { return null; }
};
const readRate = () => { const r = Number(localStorage.getItem(RATE_KEY)); return RATES.includes(r) ? r : 1; };
// Language-specific text picker so packs for any language can be built.
export const tcFor = (l) => (obj) => (typeof obj === "string" ? obj : obj?.[l] || obj?.en || obj?.id || "");

// Global Audio Handbook engine: sequential TTS playback of the manasik handbook,
// persistent across in-app navigation, remembers position, lock-screen controls,
// playback speed, sleep timer and offline packs.
export const AudiobookProvider = ({ children }) => {
  const { tc, lang } = useLang();
  const { voiceId } = useQari();
  const content = useManasikContent();
  const chapters = useMemo(() => buildChapters(tc, content), [tc, content]);
  const sig = useMemo(() => chaptersSig(chapters), [chapters]);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(() => readPos() || { c: 0, s: 0 });
  const [hasSaved, setHasSaved] = useState(() => !!readPos());
  const [status, setStatus] = useState("idle"); // idle | loading | playing | paused | finished | quota | error
  const [rate, setRateState] = useState(readRate);
  const [sleep, setSleep] = useState(null); // null | {mode:"min", until} | {mode:"chapter"}
  const [sleepLeft, setSleepLeft] = useState(0);
  const [dl, setDl] = useState(null); // {lang, done, total, bytes, status: downloading|quota|error}
  const [packVersion, setPackVersion] = useState(0);

  const audioRef = useRef(null);
  const tokenRef = useRef(0);
  const urlCache = useRef(new Map());
  const objUrlRef = useRef(null);
  const dlAbort = useRef(null);
  const syncTimer = useRef(null);
  const live = useRef({});
  live.current = { chapters, voiceId, rate, sleep, lang, sig, hasSaved, status };

  // Account sync (cross-device): last-write-wins on `t`; debounced, best-effort.
  const pushSync = (patch = {}) => {
    clearTimeout(syncTimer.current);
    const payload = { c: pos.c, s: pos.s, rate: live.current.rate, done: !live.current.hasSaved, ...patch, t: Date.now() };
    syncTimer.current = setTimeout(() => putLearn({ audiobook: payload }), 800);
  };
  useEffect(() => {
    getLearn().then((data) => {
      const r = data?.audiobook;
      if (!r || typeof r.t !== "number") return;
      if ((readPos()?.t || 0) >= r.t) return;
      if (RATES.includes(r.rate)) { setRateState(r.rate); localStorage.setItem(RATE_KEY, String(r.rate)); }
      if (live.current.status !== "idle") return;
      if (r.done || !Number.isInteger(r.c)) {
        localStorage.removeItem(POS_KEY); setHasSaved(false); setPos({ c: 0, s: 0 });
      } else {
        const p = { c: r.c, s: Number.isInteger(r.s) ? r.s : 0 };
        localStorage.setItem(POS_KEY, JSON.stringify({ ...p, t: r.t }));
        setPos(p); setHasSaved(true);
      }
    });
    return () => clearTimeout(syncTimer.current);
  }, []);

  const getAudio = () => {
    if (!audioRef.current) {
      const a = new Audio();
      a.preload = "auto";
      audioRef.current = a;
    }
    return audioRef.current;
  };

  const fetchUrl = async (text, c, s) => {
    const { lang: l, voiceId: v, sig: sg } = live.current;
    if (c != null) {
      const off = await offlineSegmentUrl(l, v, sg, c, s).catch(() => null);
      if (off) return off;
    }
    const key = `${v}|${text}`;
    if (urlCache.current.has(key)) return urlCache.current.get(key);
    const { data } = await api.post("/tts", { text, voice_id: v });
    const url = data.url.startsWith("http") ? data.url : `${BASE}${data.url}`;
    urlCache.current.set(key, url);
    return url;
  };

  const nextIndex = (c, s) => {
    const ch = live.current.chapters;
    if (s + 1 < ch[c].segments.length) return { c, s: s + 1 };
    if (c + 1 < ch.length) return { c: c + 1, s: 0 };
    return null;
  };

  // Warm the next segment (server TTS + browser buffer) so chapters flow without silence.
  const prefetch = (idx) => {
    const seg = idx && live.current.chapters[idx.c]?.segments[idx.s];
    if (!seg) return;
    fetchUrl(seg, idx.c, idx.s).then((url) => {
      if (url.startsWith("blob:")) { URL.revokeObjectURL(url); return; }
      const a = new Audio(); a.preload = "auto"; a.src = url; a.load();
    }).catch(() => {});
  };

  const savePos = (c, s) => {
    setPos({ c, s }); setHasSaved(true);
    localStorage.setItem(POS_KEY, JSON.stringify({ c, s, t: Date.now() }));
    pushSync({ c, s, done: false });
  };

  const playAt = async (c, s) => {
    const ch = live.current.chapters;
    if (!ch[c] || ch[c].segments[s] == null) return;
    const token = ++tokenRef.current;
    const audio = getAudio();
    audio.pause();
    savePos(c, s); setOpen(true); setStatus("loading");
    try {
      const url = await fetchUrl(ch[c].segments[s], c, s);
      if (token !== tokenRef.current) { if (url.startsWith("blob:")) URL.revokeObjectURL(url); return; }
      if (objUrlRef.current) { URL.revokeObjectURL(objUrlRef.current); objUrlRef.current = null; }
      if (url.startsWith("blob:")) objUrlRef.current = url;
      audio.src = url;
      audio.playbackRate = live.current.rate;
      audio.volume = 1;
      audio.onended = () => {
        if (token !== tokenRef.current) return;
        const n = nextIndex(c, s);
        if (!n) { setStatus("finished"); setHasSaved(false); localStorage.removeItem(POS_KEY); pushSync({ c: 0, s: 0, done: true }); return; }
        if (live.current.sleep?.mode === "chapter" && n.c !== c) {
          savePos(n.c, n.s); setSleep(null); setStatus("paused");
          toast(tcFor(live.current.lang)(AUDIOBOOK_UI.sleepDone));
          return;
        }
        playAt(n.c, n.s);
      };
      audio.onplaying = () => { if (token === tokenRef.current) setStatus("playing"); };
      audio.onpause = () => { if (token === tokenRef.current && !audio.ended) setStatus((st) => (st === "playing" ? "paused" : st)); };
      audio.onerror = () => { if (token === tokenRef.current) setStatus("error"); };
      await audio.play();
      prefetch(nextIndex(c, s));
    } catch (e) {
      if (token !== tokenRef.current) return;
      setStatus(e?.response?.status === 429 ? "quota" : "error");
    }
  };

  const pause = () => audioRef.current?.pause();
  const resume = () => {
    const a = audioRef.current;
    if (a && a.src && status === "paused" && !a.ended) a.play().catch(() => setStatus("error"));
    else playAt(pos.c, pos.s);
  };
  const toggle = () => {
    if (status === "loading") return;
    if (status === "playing") pause();
    else if (status === "paused") resume();
    else if (status === "finished") playAt(0, 0);
    else playAt(pos.c, pos.s);
  };
  const start = () => {
    if (status === "playing" || status === "loading") { setOpen(true); return; }
    if (status === "paused") { setOpen(true); resume(); return; }
    playAt(status === "finished" ? 0 : pos.c, status === "finished" ? 0 : pos.s);
  };
  const next = () => { if (pos.c + 1 < chapters.length) playAt(pos.c + 1, 0); };
  const prev = () => playAt(Math.max(0, pos.c - 1), 0);
  const jump = (c) => playAt(c, 0);
  const restart = () => playAt(0, 0);
  const close = () => { ++tokenRef.current; audioRef.current?.pause(); setOpen(false); setStatus("idle"); setSleep(null); };

  // Playback speed (persisted).
  const setRate = (r) => {
    setRateState(r);
    localStorage.setItem(RATE_KEY, String(r));
    if (audioRef.current) audioRef.current.playbackRate = r;
    pushSync({ rate: r });
  };
  const cycleRate = () => setRate(RATES[(RATES.indexOf(rate) + 1) % RATES.length]);

  // Sleep timer: minutes (gentle fade-out then pause) or end of the current chapter.
  const setSleepMinutes = (min) => setSleep(min ? { mode: "min", until: Date.now() + min * 60000 } : null);
  const setSleepChapter = () => setSleep({ mode: "chapter" });
  const clearSleep = () => setSleep(null);
  useEffect(() => {
    if (sleep?.mode !== "min") { setSleepLeft(0); return; }
    const tick = () => {
      const left = Math.max(0, Math.round((sleep.until - Date.now()) / 1000));
      setSleepLeft(left);
      if (left > 0) return;
      clearInterval(id);
      setSleep(null);
      const a = audioRef.current;
      if (a && !a.paused) {
        let v = a.volume;
        const fade = setInterval(() => {
          v -= 0.1;
          if (v > 0) { a.volume = Math.max(0, v); return; }
          clearInterval(fade); a.pause(); a.volume = 1;
        }, 250);
      }
      toast(tc(AUDIOBOOK_UI.sleepDone));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [sleep]); // eslint-disable-line react-hooks/exhaustive-deps

  // Offline packs.
  const pack = useMemo(() => getPack(lang), [lang, packVersion]); // eslint-disable-line react-hooks/exhaustive-deps
  const total = countSegments(chapters);
  const offline = {
    state: packState(pack, sig, voiceId, total), // none | stale | partial | done
    done: pack ? Object.keys(pack.urls).length : 0,
    total,
    bytes: pack?.bytes || 0,
    downloading: dl?.lang === lang && dl.status === "downloading",
    waiting: dl?.lang === lang && dl.status === "waiting",
    dl,
  };
  const startDownload = async (l = lang, { resumed = false } = {}) => {
    if (dlAbort.current) return;
    clearResume();
    const ctrl = new AbortController();
    dlAbort.current = ctrl;
    const chs = l === lang ? chapters : buildChapters(tcFor(l));
    setDl((d) => ({ lang: l, done: d?.lang === l ? d.done : 0, total: countSegments(chs), bytes: 0, etaSec: null, status: "downloading" }));
    if (resumed) toast.message(tcFor(live.current.lang)(AUDIOBOOK_UI.resumedNet));
    try {
      await downloadPack({
        lang: l, voiceId, chapters: chs, signal: ctrl.signal, concurrency: 3,
        onProgress: (p) => { setDl({ lang: l, ...p, status: "downloading" }); setPackVersion((v) => v + 1); },
      });
      setDl(null);
    } catch (e) {
      if (e?.name === "AbortError" || e?.code === "ERR_CANCELED") setDl(null);
      else if (isNetworkError(e) || navigator.onLine === false) {
        setDl((d) => ({ ...(d || { lang: l, done: 0, total: 0, bytes: 0 }), status: "waiting" }));
        waitForOnline(l);
      } else {
        setDl((d) => ({ ...(d || { lang: l, done: 0, total: 0, bytes: 0 }), status: e?.response?.status === 429 ? "quota" : "error" }));
      }
    } finally {
      dlAbort.current = null;
      setPackVersion((v) => v + 1);
    }
  };
  // Connection lost mid-download: resume automatically when the device is back online.
  const resumeRef = useRef(null);
  const clearResume = () => {
    const r = resumeRef.current;
    if (!r) return;
    window.removeEventListener("online", r.onOnline);
    clearInterval(r.poll);
    clearTimeout(r.timer);
    resumeRef.current = null;
  };
  const waitForOnline = (l) => {
    clearResume();
    const go = () => { if (navigator.onLine === false || dlAbort.current) return; clearResume(); startDownload(l, { resumed: true }); };
    const r = { onOnline: () => { r.timer = setTimeout(go, 1500); }, poll: setInterval(go, 20000), timer: null };
    window.addEventListener("online", r.onOnline);
    resumeRef.current = r;
  };
  const cancelDownload = () => { clearResume(); if (dlAbort.current) dlAbort.current.abort(); else setDl(null); };
  const removePack = async (l = lang) => { await deletePack(l); setDl((d) => (d?.lang === l && d.status !== "downloading" ? null : d)); setPackVersion((v) => v + 1); };

  // Lock-screen / notification controls via the Media Session API.
  const ctrl = useRef({});
  ctrl.current = { pause, resume, next, prev };
  useEffect(() => {
    const ms = navigator.mediaSession;
    if (!ms) return;
    const handlers = {
      play: () => ctrl.current.resume(),
      pause: () => ctrl.current.pause(),
      nexttrack: () => ctrl.current.next(),
      previoustrack: () => ctrl.current.prev(),
    };
    Object.entries(handlers).forEach(([k, fn]) => { try { ms.setActionHandler(k, fn); } catch (e) { logWarn("MediaSession action unsupported:", k, e); } });
    return () => {
      Object.keys(handlers).forEach((k) => { try { ms.setActionHandler(k, null); } catch (e) { logWarn("MediaSession action reset failed:", k, e); } });
      audioRef.current?.pause();
      dlAbort.current?.abort();
      clearResume();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const ms = navigator.mediaSession;
    if (!ms || !open) return;
    try {
      ms.metadata = new window.MediaMetadata({ title: chapters[pos.c]?.title || "", artist: tc(AUDIOBOOK_UI.title), album: "Manasik" });
      ms.playbackState = status === "playing" ? "playing" : "paused";
    } catch (e) { logWarn("MediaSession metadata failed:", e); }
  }, [open, pos, status, chapters, tc]);

  return (
    <Ctx.Provider value={{
      open, status, pos, chapters, hasSaved, start, toggle, next, prev, jump, restart, close,
      rate, setRate, cycleRate, sleep, sleepLeft, setSleepMinutes, setSleepChapter, clearSleep,
      offline, startDownload, cancelDownload, removePack, packVersion,
    }}>
      {children}
    </Ctx.Provider>
  );
};

export const useAudiobook = () => useContext(Ctx);
